<template>
    <button
        v-if="cell.kind === SLOT_FREE"
        type="button"
        class="w-full h-full flex items-center justify-between bg-white border border-gray-300 border-l-[3px] border-l-primary rounded-lg text-left transition hover:border-primary-300 hover:bg-primary-50 active:scale-[0.985] motion-reduce:active:scale-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-700"
        :class="size.inset"
        :aria-label="bookLabel"
        @click="$emit('book', cell)"
    >
        <span :class="size.stack">
            <span class="block font-semibold text-gray-900" :class="size.heading">Available</span>
            <span v-if="priceHint" class="block text-gray-600" :class="size.detail">{{ priceHint }}</span>
        </span>
        <span
            v-if="size.affordance"
            class="shrink-0 flex items-center justify-center rounded-full bg-primary-100"
            :class="size.affordance"
        >
            <i class="fa-solid fa-plus text-primary-800" :class="size.icon" aria-hidden="true" />
        </span>
    </button>

    <div
        v-else-if="cell.kind === SLOT_SOON"
        class="w-full h-full flex items-center gap-2 bg-gray-50 border border-dashed border-gray-400 rounded-lg"
        :class="size.insetFlat"
    >
        <i class="fa-solid fa-clock text-gray-500 shrink-0" :class="size.icon" aria-hidden="true" />
        <span class="font-medium leading-snug text-gray-600" :class="size.detail">{{ size.soonNote }}</span>
    </div>

    <div
        v-else-if="cell.kind === SLOT_PAST"
        class="w-full h-full flex items-center rounded-lg"
        :class="[size.insetFlat, size.pastFill]"
    >
        <span v-if="size.pastLabel" class="font-medium text-gray-600" :class="size.detail">{{ size.pastLabel }}</span>
    </div>

    <div
        v-else-if="cell.kind === SLOT_BOOKED"
        class="w-full h-full flex items-center gap-2 bg-gray-200 rounded-lg"
        :class="size.insetFlat"
    >
        <i class="fa-solid fa-lock text-gray-600 shrink-0" :class="size.icon" aria-hidden="true" />
        <span :class="size.stack">
            <span class="block font-semibold text-gray-800" :class="size.detail">Booked</span>
            <span v-if="size.showRange" class="block text-gray-600" :class="size.bookedDetail">{{ range }}</span>
        </span>
    </div>

    <component
        v-else
        :is="cell.eventId ? 'button' : 'div'"
        :type="cell.eventId ? 'button' : null"
        :aria-label="cell.eventId ? `View event: ${cell.name}, ${range}` : null"
        class="w-full h-full flex items-center justify-between gap-2 bg-primary rounded-lg text-left"
        :class="[
            size.inset,
            cell.eventId
                ? 'transition hover:bg-primary-700 active:scale-[0.985] motion-reduce:active:scale-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-700'
                : 'cursor-default',
        ]"
        @click="cell.eventId && $emit('viewEvent', cell)"
    >
        <span :class="size.stack">
            <span class="block font-bold leading-tight text-white" :class="[size.heading, size.clamp]">{{ cell.name }}</span>
            <span class="block text-white" :class="size.detail">{{ size.showRange ? `Event · ${range}` : "Event" }}</span>
        </span>
        <i v-if="cell.eventId" class="fa-solid fa-chevron-right shrink-0 text-white" :class="size.icon" aria-hidden="true" />
    </component>
</template>

<script setup>
    import { computed } from "vue";
    import { formatHour, SLOT_BOOKED, SLOT_FREE, SLOT_PAST, SLOT_SOON } from "../../Composables/useBookingSchedule.js";

    /**
     * What a slot *says* — the five states and their copy — kept in one file so the phone list and
     * the desktop grid cannot end up describing the same hour differently. Callers own the box:
     * this fills whatever they give it, and nothing here knows which view it is in.
     */
    const props = defineProps({
        cell: Object,
        priceHint: String,
        /** Named in the accessible label, since the grid keeps the table name in a column header. */
        spaceName: String,
        density: {
            type: String,
            default: "comfortable",
        },
    });

    defineEmits(["book", "viewEvent"]);

    // Sizing is the only thing the two boxes disagree about. Whole class strings, never assembled
    // by concatenation — Tailwind scans source text, so a name that never appears here is a name
    // that never gets generated.
    const SIZING = {
        comfortable: {
            inset: "pl-3.5 pr-3",
            insetFlat: "px-3.5",
            heading: "text-base",
            detail: "text-sm",
            bookedDetail: "text-xs",
            icon: "",
            affordance: "w-9 h-9",
            stack: "",
            clamp: "",
            pastFill: "bg-gray-100",
            pastLabel: "Past",
            showRange: true,
            soonNote: "Too soon — we need an hour's notice",
        },
        // A grid column can be as narrow as 13rem, so the compact box gives up a type step and
        // ellipsises the one thing that is not ours to shorten: whatever an event is called.
        compact: {
            inset: "px-3",
            insetFlat: "px-3",
            heading: "text-sm",
            detail: "text-xs",
            bookedDetail: "text-xs",
            icon: "text-xs",
            // Anything drawn per cell is drawn up to sixty times here, so the grid keeps only
            // what its own layout cannot already say. The row is the hour and the block is the
            // span, so neither needs writing down; the plus is a thumb target the mouse has no
            // use for, since the whole cell is the button.
            affordance: null,
            stack: "min-w-0",
            clamp: "truncate",
            // The page is bg-gray-100, so a grey box with no word in it would just be a hole.
            // The list hides finished hours behind a disclosure; the grid leaves the cell empty,
            // which says the same thing and lets the eye fall straight to what is bookable.
            pastFill: "",
            pastLabel: null,
            showRange: false,
            // Every grid row is a 1fr track, so they all settle at the tallest one. Left at the
            // full sentence this wraps to three lines at 13rem and drags the whole day with it.
            soonNote: "Too soon — 1 hr notice",
        },
    };

    const size = computed(() => SIZING[props.density] || SIZING.comfortable);

    const range = computed(() => `${formatHour(props.cell.hour)} – ${formatHour(props.cell.hour + props.cell.length)}`);

    // The hour lives outside this component in both views, so the button has to say it itself.
    const bookLabel = computed(() =>
        [
            `Book ${formatHour(props.cell.hour)}`,
            props.spaceName ? `on ${props.spaceName}` : null,
            props.priceHint,
        ]
            .filter(Boolean)
            .join(", "),
    );
</script>
